import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import { orderBy } from 'lodash';

import type {
  PortStaticInfo,
  CarrierStaticInfo,
  CarrierNameResult,
  AirportStaticInfo,
  FlightAutoCompleteSuggestion,
  Coordinate,
} from '@libs/trip-planner-interfaces';

import { ContentApi } from './content/contentApi';
import { ApiQuery } from './shared/apiQuery';
import { camelCased } from './shared/apiQuery.helper';

export class FlightEndpoint {
  private contentApi = new ContentApi();
  private apiQuery = new ApiQuery();

  public searchCarriersStaticInfo(
    carrierCodes: string[]
  ): Observable<CarrierStaticInfo[]> {
    const url = 'mvc/Flight/GetCarrierInfoFromCarrierCodes';
    return this.apiQuery
      .postObservable<CarrierNamesPayload, CarrierStaticInfo[]>(url, {
        carrierCodes,
      })
      .pipe(camelCased({ deep: true }));
  }

  public searchCarrierNames(
    carrierCodes: string[]
  ): Observable<CarrierNameResult> {
    const url = 'mvc/Flight/GetCarriersNamesFromCarrierCodes';
    return this.apiQuery
      .postObservable<CarrierNamesPayload, CarrierNameResult>(url, {
        carrierCodes,
      })
      .pipe(camelCased({ deep: true }));
  }

  public searchAirportStaticInfo(iata: string): Observable<PortStaticInfo> {
    return this.contentApi.searchIataStaticInfo(iata);
  }

  public searchSuggestions(
    searchTerm: string
  ): Observable<FlightAutoCompleteSuggestion[]> {
    return this.contentApi.searchAirportSuggestions(searchTerm);
  }

  public searchSuggestionsByPosition(
    position: Coordinate
  ): Observable<FlightAutoCompleteSuggestion[]> {
    return this.contentApi.searchAirportSuggestionsByPosition(position);
  }

  public searchAirportsForDestination(
    searchTerm: string,
    size: number
  ): Observable<AirportStaticInfo[]> {
    return this.contentApi.searchAirportStaticInfoForDestination(
      searchTerm,
      size
    );
  }

  public getSuggestedAirport(
    findBy: string | Coordinate
  ): Observable<AirportStaticInfo | undefined> {
    const closestAirportsAmount = 5;
    const method =
      typeof findBy === 'string'
        ? this.contentApi.searchAirportStaticInfoForDestination(findBy, 1)
        : this.contentApi.searchAirportStaticInfoForPosition(
            findBy,
            closestAirportsAmount
          );

    return method.pipe(
      map((data: AirportStaticInfo[]) => {
        if (data.length) {
          const sorted = orderBy(data, 'popularity', 'desc');

          return sorted[0];
        }

        return undefined;
      })
    );
  }
}

interface CarrierNamesPayload {
  carrierCodes: string[];
}
